import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { CategoryListInterface } from "../../common/interfaces/CategoryListInterface";
import { ProductInterface } from "../../common/interfaces/ProductInterface";
import { ProductRangeSubCategoryMapper } from "../../common/utils/productRangeSubCategoryMapper";
import { loadProductImages } from "../../common/utils/imageLoader";
import { FilterListInterface } from "../../common/interfaces/FilterListInterface";
import {
    applyFilters,
    filterCategoriesByType,
} from "../../common/utils/filterCategories";

export enum ProductTypeEnum {
    MACHINES = "machines",
    ACCESSORIES = "accessories",
}

export const setFilteredCategories = createAsyncThunk(
    "productRange/setFilteredCategories",
    async (payload: {
        categories: CategoryListInterface[];
        products: ProductInterface[];
        productType: ProductTypeEnum;
        getLocalImageUrl: (cdnUrl: string) => Promise<string | null>;
        filters?: FilterListInterface[];
        selectedFilters?: {
            [filterId: string]: {
                id: number;
                options: { id: string; value: string | number }[];
            };
        };
    }) => {
        const {
            categories,
            products,
            productType,
            getLocalImageUrl,
            filters = [],
            selectedFilters = {},
        } = payload;

        const productImages = await loadProductImages(
            products,
            getLocalImageUrl,
        );
        const filteredProducts = applyFilters(
            products,
            filters,
            selectedFilters,
        );
        const filteredCategories = filterCategoriesByType(
            categories,
            productType,
        );

        const processedCategories = filteredCategories.map(
            (category: CategoryListInterface) => {
                const categoryProducts = filteredProducts.filter(
                    (product: ProductInterface) =>
                        product.categories.includes(category.id),
                );

                const updatedCategory = ProductRangeSubCategoryMapper(
                    filteredProducts,
                    category,
                );

                return {
                    ...updatedCategory,
                    products: categoryProducts,
                };
            },
        );

        return {
            filteredCategories: processedCategories,
            productImages,
        };
    },
);

interface ProductRangePageInterface {
    gridLayout: boolean;
    productType: ProductTypeEnum;
    filteredCategories: CategoryListInterface[];
    productImages: { [key: string]: string };
    isProcessingCategories: boolean;
    filters: FilterListInterface[];
    selectedFilters: {
        [filterId: string]: {
            id: number;
            options: { id: string; value: string | number }[];
        };
    };
    mobileFiltersActive: boolean;
}

const initialState: ProductRangePageInterface = {
    gridLayout: true,
    productType: ProductTypeEnum.MACHINES,
    filteredCategories: [],
    productImages: {},
    isProcessingCategories: false,
    filters: [],
    selectedFilters: {},
    mobileFiltersActive: false,
};

const productRangeSlice = createSlice({
    name: "productRange",
    initialState,
    reducers: {
        setGridLayout: (state, action: PayloadAction<boolean>) => {
            state.gridLayout = action.payload;
        },
        setProductType: (state, action: PayloadAction<ProductTypeEnum>) => {
            state.productType = action.payload;
        },
        setFilters: (state, action: PayloadAction<FilterListInterface[]>) => {
            state.filters = action.payload;
        },
        selectFilter: (
            state,
            action: PayloadAction<{
                filter: { id: number };
                option: { id: string; value: string | number };
            }>,
        ) => {
            const filterId = action.payload.filter.id;
            const option = action.payload.option;

            if (!state.selectedFilters[filterId]) {
                state.selectedFilters[filterId] = {
                    id: filterId,
                    options: [option],
                };
                return;
            }

            const hasOption = state.selectedFilters[filterId].options.some(
                (selectedOption) => selectedOption.id === option.id,
            );

            if (hasOption) {
                const remainingOptions = state.selectedFilters[
                    filterId
                ].options.filter(
                    (selectedOption) => selectedOption.id !== option.id,
                );

                if (remainingOptions.length === 0) {
                    delete state.selectedFilters[filterId];
                    return;
                }

                state.selectedFilters[filterId] = {
                    id: filterId,
                    options: remainingOptions,
                };
                return;
            }

            state.selectedFilters[filterId] = {
                id: filterId,
                options: [...state.selectedFilters[filterId].options, option],
            };
        },
        resetFilter: (state) => {
            state.selectedFilters = {};
        },
        setMobileFiltersActive: (state, action: PayloadAction<boolean>) => {
            state.mobileFiltersActive = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setFilteredCategories.pending, (state) => {
                state.isProcessingCategories = true;
            })
            .addCase(setFilteredCategories.fulfilled, (state, action) => {
                state.filteredCategories = action.payload.filteredCategories;
                state.productImages = action.payload.productImages;
                state.isProcessingCategories = false;
            })
            .addCase(setFilteredCategories.rejected, (state) => {
                state.isProcessingCategories = false;
            });
    },
});

export const productRangeReducer = productRangeSlice.reducer;

export const {
    setGridLayout,
    setProductType,
    setFilters,
    selectFilter,
    resetFilter,
    setMobileFiltersActive,
} = productRangeSlice.actions;
